import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { services, solutionsMenu } from "@/data/site";
import { scrollToHash, scrollTop } from "@/lib/scroll";
import MobileMenu from "./MobileMenu";
import { EASE } from "./motion";

export function Wordmark({ light = false }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-[9px] shadow-[0_6px_16px_-6px_rgba(124,58,237,0.55)]">
        <img src="/bytevora-icon.png" alt="Bytevora logo" className="h-full w-full object-cover" />
      </span>
      <span
        className={`text-[16px] font-extrabold tracking-[0.16em] ${
          light ? "text-white" : "text-slate-900"
        }`}
      >
        BYTEVORA
      </span>
    </span>
  );
}

export function useSectionNav() {
  const navigate = useNavigate();
  const location = useLocation();
  return (hash) => {
    if (location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => scrollToHash(hash), 200);
    } else {
      scrollToHash(hash);
    }
  };
}

function DropdownShell({ label, testId, onNavigate, wide, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={() => {
          setOpen(false);
          onNavigate();
        }}
        onFocus={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-2 text-[14.5px] font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
      >
        {label}
        <ChevronDown
          size={14}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <div
            className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 ${
              wide ? "w-[620px]" : "w-[400px]"
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.28, ease: EASE }}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-[0_28px_70px_-24px_rgba(11,18,32,0.3)] backdrop-blur-xl"
            >
              {children(() => setOpen(false))}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const go = useSectionNav();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || mobileOpen || location.pathname !== "/";

  const serviceColumns = [
    {
      groups: [
        { label: "Build", items: [services[0], services[1]] },
        { label: "Intelligence", items: [services[2]] },
      ],
    },
    {
      groups: [
        { label: "Infrastructure", items: [services[3]] },
        { label: "Transform", items: [services[4], services[5]] },
      ],
    },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color] duration-300 ${
          solid
            ? "border-b border-slate-200/80 bg-white/90 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] w-full max-w-[1200px] items-center justify-between px-6 md:px-8">
          <Link
            to="/"
            data-testid="header-logo"
            aria-label="Bytevora homepage"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                scrollTop(false);
              }
            }}
          >
            <Wordmark />
          </Link>

          <nav
            className="hidden items-center gap-1 lg:flex"
            data-testid="desktop-nav"
            aria-label="Primary"
          >
            <DropdownShell
              label="Services"
              testId="nav-services-dropdown"
              onNavigate={() => navigate("/services")}
              wide
            >
              {(close) => (
                <div className="grid grid-cols-2 gap-1 p-4">
                  {serviceColumns.map((col, ci) => (
                    <div key={ci} className="flex flex-col gap-1">
                      {col.groups.map((group) => (
                        <div key={group.label}>
                          <p className="px-3 pb-1.5 pt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">
                            {group.label}
                          </p>
                          {group.items.map((s) => (
                            <button
                              key={s.slug}
                              type="button"
                              data-testid={`nav-service-${s.slug}`}
                              onClick={() => {
                                close();
                                navigate(`/services#${s.slug}`);
                              }}
                              className="group flex w-full items-start gap-3.5 rounded-xl p-3 text-left transition-colors duration-200 hover:bg-slate-50"
                            >
                              <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors duration-200 group-hover:border-blue-600/30 group-hover:text-blue-600">
                                <s.icon size={16} strokeWidth={1.8} />
                              </span>
                              <span>
                                <span className="block text-[13.5px] font-semibold text-slate-900">
                                  {s.title}
                                </span>
                                <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
                                  {s.menuDesc}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </DropdownShell>

            <DropdownShell
              label="Solutions"
              testId="nav-solutions-dropdown"
              onNavigate={() => navigate("/solutions")}
            >
              {(close) => (
                <div className="flex flex-col gap-1 p-4">
                  {solutionsMenu.map((s, i) => (
                    <button
                      key={s.title}
                      type="button"
                      data-testid={`nav-solution-${i}`}
                      onClick={() => {
                        close();
                        navigate(`/solutions#${s.slug}`);
                      }}
                      className="group flex w-full items-start gap-3.5 rounded-xl p-3 text-left transition-colors duration-200 hover:bg-slate-50"
                    >
                      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors duration-200 group-hover:border-violet-600/30 group-hover:text-violet-600">
                        <s.icon size={16} strokeWidth={1.8} />
                      </span>
                      <span>
                        <span className="block text-[13.5px] font-semibold text-slate-900">
                          {s.title}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
                          {s.desc}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </DropdownShell>

            <Link
              to="/products"
              data-testid="nav-products-link"
              className="px-3 py-2 text-[14.5px] font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
            >
              Products
            </Link>
            <Link
              to="/about"
              data-testid="nav-about-link"
              className="px-3 py-2 text-[14.5px] font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
            >
              About
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact-link"
              className="px-3 py-2 text-[14.5px] font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
            >
              Contact
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="header-start-project-button"
              onClick={() => navigate("/contact")}
              className="group hidden items-center gap-2 rounded-[10px] bg-navy px-5 py-2.5 text-[13.5px] font-semibold text-white transition-colors duration-300 hover:bg-blue-600 sm:inline-flex"
            >
              Start a Project
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className="grid h-10 w-10 place-items-center rounded-[10px] border border-slate-200 text-slate-900 transition-colors duration-200 hover:border-slate-400 lg:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        go={go}
      />
    </>
  );
}
